import { LanCode } from "@/store/reducers/setting";
import { LocalizedSeoData, SeoCategory } from "@/types/seo";

/**
 * 정적 페이지 SEO 데이터
 * (기존 PageUtils.ts에서 마이그레이션)
 */
export const staticSeoData: Partial<Record<SeoCategory, LocalizedSeoData>> = {
  /**
   * 홈(`/`) 전용 — 언어별 title · description.
   *
   * 2026-02-26(`6ed04b9b0`)부터 홈이 영어 `seo` 를 직접 넘겨 7개 언어가 전부 영어였다. 한국어 검색(「비햅틱스」)에서
   * 설명이 검색어와 안 맞아 구글이 푸터(뉴스레터 동의 · 사업자 정보)를 스니펫으로 골랐다. 영어 값은 그때 문구 그대로다.
   */
  Home: {
    [LanCode.Chinese]: {
      title: "bHaptics TactSuit | 全身感受游戏与VR的触感套装",
      description:
        "使用 bHaptics TactSuit，全身感受游戏和VR中的每一次冲击。32个振动马达，支持250多款VR游戏。触感套装、手套及配件。",
    },
    [LanCode.English]: {
      title: "Next Generation Full Body Haptic Suit - bHaptics TactSuit",
      description:
        "Experience immersive haptic feedback with bHaptics TactSuit. 32 motors, 250+ compatible VR games. Full body haptic vest, gloves, and accessories.",
    },
    [LanCode.French]: {
      title: "bHaptics TactSuit | Gilet haptique pour le jeu et la VR",
      description:
        "Ressentez le jeu et la VR dans tout votre corps avec le bHaptics TactSuit. 32 moteurs de vibration, plus de 250 jeux VR compatibles. Gilets haptiques, gants et accessoires.",
    },
    [LanCode.German]: {
      title: "bHaptics TactSuit | Haptik-Anzug für Games und VR",
      description:
        "Spüre Games und VR mit dem ganzen Körper – mit der bHaptics TactSuit. 32 Vibrationsmotoren, über 250 kompatible VR-Spiele. Haptik-Anzüge, Handschuhe und Zubehör.",
    },
    [LanCode.Japanese]: {
      title: "bHaptics TactSuit | ゲームとVRを全身で感じるハプティクススーツ",
      description:
        "bHaptics TactSuitで、ゲームやVRの衝撃を全身で体感。32個の振動モーター、250以上のVRゲームに対応。ハプティクススーツ、グローブ、アクセサリー。",
    },
    [LanCode.Korean]: {
      title: "비햅틱스 TactSuit | 게임과 VR을 온몸으로 느끼는 햅틱 수트",
      description:
        "비햅틱스 TactSuit로 게임과 VR 속 사운드와 액션을 온몸으로 느껴보세요. 32개 진동 모터, 250개 이상의 VR 게임 지원. 햅틱 수트, 장갑, 액세서리까지.",
    },
    [LanCode.Spanish]: {
      title: "bHaptics TactSuit | Chaleco háptico para juegos y VR",
      description:
        "Siente los juegos y la VR en todo tu cuerpo con bHaptics TactSuit. 32 motores de vibración y más de 250 juegos VR compatibles. Chalecos hápticos, guantes y accesorios.",
    },
  },
  Compatibility: {
    [LanCode.Chinese]: {
      title: "体验 TactSuit",
      description:
        "VR、PC、电影和音乐 - TactSuit 体验。迄今为止，超过250个SteamVR和Oculus Quest标题，包括《Onward》和《Pavlov》，提供原生支持。",
    },
    [LanCode.English]: {
      title: "Experiences with TactSuit",
      description:
        "VR, PC, Movie and Music - Experiences with Tactsuit. Thus far, more than 250 SteamVR and Oculus Quest titles, including Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward and Pavlov, provide native support.",
    },
    [LanCode.French]: {
      title: "Expériences avec TactSuit",
      description:
        "VR, PC, Film et Musique - Expériences avec TactSuit. À ce jour, plus de 250 titres SteamVR et Oculus Quest, dont Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward et Pavlov, offrent un support natif.",
    },
    [LanCode.German]: {
      title: "Erfahrungen mit TactSuit",
      description:
        "VR, PC, Film und Musik - Erfahrungen mit TactSuit. Bisher bieten mehr als 250 SteamVR- und Oculus Quest-Titel, darunter Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward und Pavlov, native Unterstützung.",
    },
    [LanCode.Japanese]: {
      title: "TactSuit での体験",
      description:
        "VR、PC、映画、音楽 - TactSuit での体験。これまで、Onward、Pavlov を含む250以上の SteamVR および Oculus Quest タイトルがネイティブサポートを提供しています。",
    },
    [LanCode.Korean]: {
      title: "TactSuit 체험",
      description:
        "VR, PC, 영화 및 음악 - TactSuit 체험. 지금까지 Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward 및 Pavlov를 포함하여 250개 이상의 SteamVR 및 Oculus Quest 타이틀이 기본 지원을 제공합니다.",
    },
    [LanCode.Spanish]: {
      title: "Experiencias con TactSuit",
      description:
        "VR, PC, Película y Música - Experiencias con TactSuit. Hasta ahora, más de 250 títulos de SteamVR y Oculus Quest, incluidos Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward y Pavlov, ofrecen soporte nativo.",
    },
  },
  General: {
    [LanCode.Chinese]: {
      title: "下一代全身触觉服 - bHaptics TactSuit",
      description:
        "bHaptics 触觉背心具有32个振动触觉电机，无延迟无线连接，带来最佳的触觉体验。迄今为止，超过250个SteamVR和Oculus Quest标题，包括《Onward》和《Pavlov》，提供原生支持。我们计划继续与其他标题合作，为每款游戏提供优化的触觉反馈。bHaptics服装因其原生支持而在VR用户中最受欢迎，触觉服可以用于任何类型的平台，得益于公司的专利待批音频到触觉软件。该软件将内容的音频输出实时转换为触觉反馈，使用户在听音乐、观看电影或玩PC/主机/VR游戏时，即使没有原生支持，也能接收到基于音频的触觉反馈。",
    },
    [LanCode.English]: {
      title: "Next generation full body haptic suit - bHaptics TactSuit",
      description:
        "The bHaptics vest features 32 motors and wireless connectivity, supporting 250+ VR titles natively. Audio-to-Haptics enables haptics from any content, even without native support.",
    },
    [LanCode.French]: {
      title:
        "Combinaison haptique intégrale de nouvelle génération - bHaptics TactSuit",
      description:
        "Le gilet haptique bHaptics dispose de 32 moteurs vibro-tactiles, d'une connectivité sans fil sans latence pour offrir la meilleure expérience haptique possible. À ce jour, plus de 250 titres SteamVR et Oculus Quest, dont Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward et Pavlov, offrent un support natif. Nous prévoyons de continuer à travailler avec d'autres titres pour fournir à ses utilisateurs un retour haptique optimisé pour chaque jeu. La combinaison bHaptics est la plus populaire parmi les utilisateurs de VR en raison du support natif ; la combinaison haptique peut être utilisée avec tout type de plateforme, grâce au logiciel Audio-to-Haptics en instance de brevet de l'entreprise. Le logiciel convertit la sortie audio du contenu en retour haptique en temps réel, permettant aux utilisateurs de recevoir des haptiques basées sur l'audio lorsqu'ils écoutent de la musique, regardent des films ou jouent à des jeux PC/console/VR sans support natif.",
    },
    [LanCode.German]: {
      title: "Next Generation Ganzkörper-Haptikanzug - bHaptics TactSuit",
      description:
        "Die bHaptics-Haptikweste verfügt über 32 vibro-taktile Motoren und eine kabellose Verbindung ohne Verzögerung, um das bestmögliche Haptikerlebnis zu bieten. Bisher bieten mehr als 250 SteamVR- und Oculus Quest-Titel, darunter Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward und Pavlov, native Unterstützung. Wir planen, weiterhin mit anderen Titeln zusammenzuarbeiten, um den Benutzern optimiertes haptisches Feedback für jedes Spiel zu bieten. Der bHaptics-Anzug ist aufgrund der nativen Unterstützung bei VR-Benutzern am beliebtesten; der Haptikanzug kann dank der patentierten Audio-to-Haptics-Software des Unternehmens mit jeder Art von Plattform verwendet werden. Die Software wandelt die Audioausgabe von Inhalten in Echtzeit in haptisches Feedback um, sodass Benutzer beim Musikhören, Filme schauen oder PC-/Konsolen-/VR-Spiele spielen haptisches Feedback auf Audio-Basis erhalten, auch ohne native Unterstützung.",
    },
    [LanCode.Japanese]: {
      title: "次世代フルボディハプティックスーツ - bHaptics TactSuit",
      description:
        "bHaptics のハプティックベストは 32 個の振動触覚モーターを搭載し、遅延のないワイヤレス接続で最高の触覚体験を提供します。これまでに、Onward、Pavlov を含む 250 を超える SteamVR および Oculus Quest タイトルがネイティブサポートを提供しています。私たちは他のタイトルと協力し続けて、それぞれのゲームに最適化された触覚フィードバックをユーザーに提供する予定です。bHaptics スーツはネイティブサポートにより VR ユーザーの間で最も人気があります。企業の特許出願中のオーディオから触覚へのソフトウェアのおかげで、触覚スーツはあらゆるタイプのプラットフォームで使用できます。このソフトウェアはコンテンツのオーディオ出力をリアルタイムで触覚フィードバックに変換し、音楽を聴いたり、映画を見たり、PC/コンソール/VR ゲームをネイティブサポートなしでプレイしたりしても、オーディオベースの触覚を受け取ることができます。",
    },
    [LanCode.Korean]: {
      title: "차세대 전신 햅틱 수트 - bHaptics TactSuit",
      description:
        "bHaptics (비햅틱스) 햅틱 조끼는 32개의 진동 촉각 모터와 지연 없는 무선 연결을 특징으로 하여 가능한 최고의 촉각 경험을 제공합니다. 지금까지 Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward 및 Pavlov를 포함하여 250개 이상의 SteamVR 및 Oculus Quest 타이틀이 기본 지원을 제공합니다. 우리는 각 게임에 최적화된 촉각 피드백을 사용자에게 제공하기 위해 다른 타이틀과 계속 협력할 계획입니다. bHaptics 수트는 네이티브 지원으로 인해 VR 사용자들 사이에서 가장 인기가 많습니다. 햅틱 수트는 회사의 특허 출원 중인 오디오-햅틱 소프트웨어 덕분에 모든 유형의 플랫폼에서 사용할 수 있습니다. 이 소프트웨어는 콘텐츠의 오디오 출력을 실시간으로 촉각 피드백으로 변환하여 사용자가 음악을 듣거나 영화를 보거나 네이티브 지원 없이 PC/콘솔/VR 게임을 플레이할 때 오디오 기반 햅틱을 받을 수 있습니다.",
    },
    [LanCode.Spanish]: {
      title:
        "Traje háptico de cuerpo completo de próxima generación - bHaptics TactSuit",
      description:
        "El chaleco háptico bHaptics cuenta con 32 motores vibro-táctiles, conectividad inalámbrica sin retraso para brindar la mejor experiencia háptica posible. Hasta ahora, más de 250 títulos de SteamVR y Oculus Quest, incluidos Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward y Pavlov, ofrecen soporte nativo. Planeamos seguir trabajando con otros títulos para proporcionar a sus usuarios comentarios hápticos optimizados para cada juego. El traje de bHaptics es el más popular entre los usuarios de VR debido al soporte nativo; el traje háptico se puede utilizar con cualquier tipo de plataforma, gracias al software Audio-to-Haptics pendiente de patente de la empresa. El software convierte la salida de audio del contenido en retroalimentación háptica en tiempo real, lo que permite a los usuarios recibir retroalimentación háptica basada en audio cuando escuchan música, ven películas o juegan en PC/consola/VR sin soporte nativo.",
    },
  },
  News: {
    [LanCode.Chinese]: {
      title: "bHaptics 新闻 - 关于 bHaptics 的一切",
      description:
        "关于我们的所有信息。迄今为止，超过250个SteamVR和Oculus Quest标题，包括《Onward》和《Pavlov》，提供原生支持。",
    },
    [LanCode.English]: {
      title: "News about bHaptics - All about bHaptics",
      description:
        "All about us. Thus far, more than 250 SteamVR and Oculus Quest titles, including Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward and Pavlov, provide native support.",
    },
    [LanCode.French]: {
      title: "Nouvelles sur bHaptics - Tout sur bHaptics",
      description:
        "Tout sur nous. À ce jour, plus de 250 titres SteamVR et Oculus Quest, dont Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward et Pavlov, offrent un support natif.",
    },
    [LanCode.German]: {
      title: "Nachrichten über bHaptics - Alles über bHaptics",
      description:
        "Alles über uns. Bisher bieten mehr als 250 SteamVR- und Oculus Quest-Titel, darunter Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward und Pavlov, native Unterstützung.",
    },
    [LanCode.Japanese]: {
      title: "bHaptics に関するニュース - bHaptics のすべて",
      description:
        "私たちに関するすべて。これまでに、Onward、Pavlov を含む250以上の SteamVR および Oculus Quest タイトルがネイティブサポートを提供しています。",
    },
    [LanCode.Korean]: {
      title: "bHaptics에 관한 뉴스 - bHaptics에 관한 모든 것",
      description:
        "bHaptics(비햅틱스) 에 관한 모든 것. 지금까지 Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward 및 Pavlov를 포함하여 250개 이상의 SteamVR 및 Oculus Quest 타이틀이 기본 지원을 제공합니다.",
    },
    [LanCode.Spanish]: {
      title: "Noticias sobre bHaptics - Todo sobre bHaptics",
      description:
        "Todo sobre nosotros. Hasta ahora, más de 250 títulos de SteamVR y Oculus Quest, incluidos Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward y Pavlov, ofrecen soporte nativo.",
    },
  },
  Shop: {
    [LanCode.Chinese]: {
      title: "购买下一代全身触觉服 - bHaptics TactSuit",
      description:
        "购买用于VR、PC游戏、音乐和电影的下一代全身触觉服。迄今为止，超过250个SteamVR和Oculus Quest标题，包括《Onward》和《Pavlov》，提供原生支持。",
    },
    [LanCode.English]: {
      title: "Buy next generation full body haptic suit - bHaptics TactSuit",
      description:
        "Buy next generation full body haptic suit for VR, PC game, music and Movie. Thus far, more than 250 SteamVR and Oculus Quest titles, including Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward and Pavlov, provide native support.",
    },
    [LanCode.French]: {
      title:
        "Achetez la combinaison haptique intégrale de nouvelle génération - bHaptics TactSuit",
      description:
        "Achetez la combinaison haptique intégrale de nouvelle génération pour les jeux VR, PC, la musique et le cinéma. À ce jour, plus de 250 titres SteamVR et Oculus Quest, dont Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward et Pavlov, offrent un support natif.",
    },
    [LanCode.German]: {
      title:
        "Kaufen Sie den Ganzkörper-Haptikanzug der nächsten Generation - bHaptics TactSuit",
      description:
        "Kaufen Sie den Ganzkörper-Haptikanzug der nächsten Generation für VR, PC-Spiele, Musik und Filme. Bisher bieten mehr als 250 SteamVR- und Oculus Quest-Titel, darunter Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward und Pavlov, native Unterstützung.",
    },
    [LanCode.Japanese]: {
      title: "次世代のフルボディハプティックスーツを購入 - bHaptics TactSuit",
      description:
        "次世代のフルボディハプティックスーツをVR、PCゲーム、音楽、映画用に購入してください。これまでに、Onward、Pavlov を含む 250 を超える SteamVR および Oculus Quest タイトルがネイティブサポートを提供しています。",
    },
    [LanCode.Korean]: {
      title: "차세대 전신 햅틱 수트 구매 - bHaptics TactSuit",
      description:
        "VR, PC 게임, 음악 및 영화를 위한 차세대 전신 햅틱 수트를 구매하세요. 지금까지 Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward 및 Pavlov를 포함하여 250개 이상의 SteamVR 및 Oculus Quest 타이틀이 기본 지원을 제공합니다.",
    },
    [LanCode.Spanish]: {
      title:
        "Compre el traje háptico de cuerpo completo de próxima generación - bHaptics TactSuit",
      description:
        "Compre el traje háptico de cuerpo completo de próxima generación para juegos de VR, PC, música y películas. Hasta ahora, más de 250 títulos de SteamVR y Oculus Quest, incluidos Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward y Pavlov, ofrecen soporte nativo.",
    },
  },
  Software: {
    [LanCode.Chinese]: {
      title: "TactSuit 体验",
      description:
        "VR、PC、电影和音乐 - TactSuit 体验。迄今为止，超过250个SteamVR和Oculus Quest标题，包括《Onward》和《Pavlov》，提供原生支持。",
    },
    [LanCode.English]: {
      title: "Experiences with TactSuit",
      description:
        "VR, PC, Movie and Music - Experiences with Tactsuit. Thus far, more than 250 SteamVR and Oculus Quest titles, including Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward and Pavlov, provide native support.",
    },
    [LanCode.French]: {
      title: "Expériences avec TactSuit",
      description:
        "VR, PC, Film et Musique - Expériences avec TactSuit. À ce jour, plus de 250 titres SteamVR et Oculus Quest, dont Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward et Pavlov, offrent un support natif.",
    },
    [LanCode.German]: {
      title: "Erfahrungen mit TactSuit",
      description:
        "VR, PC, Film und Musik - Erfahrungen mit TactSuit. Bisher bieten mehr als 250 SteamVR- und Oculus Quest-Titel, darunter Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward und Pavlov, native Unterstützung.",
    },
    [LanCode.Japanese]: {
      title: "TactSuit での体験",
      description:
        "VR、PC、映画、音楽 - TactSuit での体験。これまでに、Onward、Pavlov を含む250以上の SteamVR および Oculus Quest タイトルがネイティブサポートを提供しています。",
    },
    [LanCode.Korean]: {
      title: "TactSuit 경험하기",
      description:
        "VR, PC, 영화 및 음악 - TactSuit 체험. 지금까지 Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward 및 Pavlov를 포함하여 250개 이상의 SteamVR 및 Oculus Quest 타이틀이 기본 지원을 제공합니다.",
    },
    [LanCode.Spanish]: {
      title: "Experiencias con TactSuit",
      description:
        "VR, PC, Película y Música - Experiencias con TactSuit. Hasta ahora, más de 250 títulos de SteamVR y Oculus Quest, incluidos Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward y Pavlov, ofrecen soporte nativo.",
    },
  },
  Support: {
    [LanCode.Chinese]: {
      title: "常见问题、下载、请求等",
      description:
        "常见问题、下载、请求等。迄今为止，超过250个SteamVR和Oculus Quest标题，包括《Onward》和《Pavlov》，提供原生支持。",
    },
    [LanCode.English]: {
      title: "Faqs, downloads, requests and etc",
      description:
        "Faqs, downloads, requests and etc. Thus far, more than 250 SteamVR and Oculus Quest titles, including Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward and Pavlov, provide native support.",
    },
    [LanCode.French]: {
      title: "Faqs, téléchargements, demandes, etc.",
      description:
        "Faqs, téléchargements, demandes, etc. À ce jour, plus de 250 titres SteamVR et Oculus Quest, dont Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward et Pavlov, offrent un support natif.",
    },
    [LanCode.German]: {
      title: "FAQs, Downloads, Anfragen usw.",
      description:
        "FAQs, Downloads, Anfragen usw. Bisher bieten mehr als 250 SteamVR- und Oculus Quest-Titel, darunter Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward und Pavlov, native Unterstützung.",
    },
    [LanCode.Japanese]: {
      title: "よくある質問、ダウンロード、リクエストなど",
      description:
        "よくある質問、ダウンロード、リクエストなど。これまでに、Onward、Pavlov を含む250以上の SteamVR および Oculus Quest タイトルがネイティブサポートを提供しています。",
    },
    [LanCode.Korean]: {
      title: "자주 묻는 질문, 다운로드, 요청 등",
      description:
        "자주 묻는 질문, 다운로드, 요청 등. 지금까지 Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward 및 Pavlov를 포함하여 250개 이상의 SteamVR 및 Oculus Quest 타이틀이 기본 지원을 제공합니다.",
    },
    [LanCode.Spanish]: {
      title: "Preguntas frecuentes, descargas, solicitudes, etc.",
      description:
        "Preguntas frecuentes, descargas, solicitudes, etc. Hasta ahora, más de 250 títulos de SteamVR y Oculus Quest, incluidos Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward y Pavlov, ofrecen soporte nativo.",
    },
  },
  Tactsuit: {
    [LanCode.Chinese]: {
      title: "最先进的全身触觉服 - bHaptics TactSuit",
      description:
        "最先进的触觉服。迄今为止，超过250个SteamVR和Oculus Quest标题，包括《Onward》和《Pavlov》，提供原生支持。",
    },
    [LanCode.English]: {
      title: "Most Advanced Full Body Haptic Suit - bHaptics TactSuit",
      description:
        "Most Advanced Haptic Suit. Thus far, more than 250 SteamVR and Oculus Quest titles, including Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward and Pavlov, provide native support.",
    },
    [LanCode.French]: {
      title:
        "Combinaison haptique intégrale la plus avancée - bHaptics TactSuit",
      description:
        "Combinaison haptique la plus avancée. À ce jour, plus de 250 titres SteamVR et Oculus Quest, dont Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward et Pavlov, offrent un support natif.",
    },
    [LanCode.German]: {
      title: "Fortschrittlichster Ganzkörper-Haptikanzug - bHaptics TactSuit",
      description:
        "Fortschrittlichster Haptikanzug. Bisher bieten mehr als 250 SteamVR- und Oculus Quest-Titel, darunter Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward und Pavlov, native Unterstützung.",
    },
    [LanCode.Japanese]: {
      title: "最先端のフルボディハプティックスーツ - bHaptics TactSuit",
      description:
        "最先端の触覚スーツ。これまでに、Ghosts of Tabor, Breachers, Arizona Sunshine2、Onward、Pavlov を含む250以上の SteamVR および Oculus Quest タイトルがネイティブサポートを提供しています。",
    },
    [LanCode.Korean]: {
      title: "가장 진보된 전신 햅틱 수트 - bHaptics TactSuit",
      description:
        "가장 진보된 햅틱 수트. 지금까지 Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward 및 Pavlov를 포함하여 250개 이상의 SteamVR 및 Oculus Quest 타이틀이 기본 지원을 제공합니다.",
    },
    [LanCode.Spanish]: {
      title:
        "Traje háptico de cuerpo completo más avanzado - bHaptics TactSuit",
      description:
        "Traje háptico más avanzado. Hasta ahora, más de 250 títulos de SteamVR y Oculus Quest, incluidos Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward y Pavlov, ofrecen soporte nativo.",
    },
  },
  // Games 카테고리도 Compatibility와 동일하게 사용
  Games: {
    [LanCode.Chinese]: {
      title: "体验 TactSuit",
      description:
        "VR、PC、电影和音乐 - TactSuit 体验。迄今为止，超过250个SteamVR和Oculus Quest标题，包括《Onward》和《Pavlov》，提供原生支持。",
    },
    [LanCode.English]: {
      title: "Experiences with TactSuit",
      description:
        "VR, PC, Movie and Music - Experiences with Tactsuit. Thus far, more than 250 SteamVR and Oculus Quest titles, including Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward and Pavlov, provide native support.",
    },
    [LanCode.French]: {
      title: "Expériences avec TactSuit",
      description:
        "VR, PC, Film et Musique - Expériences avec TactSuit. À ce jour, plus de 250 titres SteamVR et Oculus Quest, dont Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward et Pavlov, offrent un support natif.",
    },
    [LanCode.German]: {
      title: "Erfahrungen mit TactSuit",
      description:
        "VR, PC, Film und Musik - Erfahrungen mit TactSuit. Bisher bieten mehr als 250 SteamVR- und Oculus Quest-Titel, darunter Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward und Pavlov, native Unterstützung.",
    },
    [LanCode.Japanese]: {
      title: "TactSuit での体験",
      description:
        "VR、PC、映画、音楽 - TactSuit での体験。これまで、Onward、Pavlov を含む250以上の SteamVR および Oculus Quest タイトルがネイティブサポートを提供しています。",
    },
    [LanCode.Korean]: {
      title: "TactSuit 체험",
      description:
        "VR, PC, 영화 및 음악 - TactSuit 체험. 지금까지 Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward 및 Pavlov를 포함하여 250개 이상의 SteamVR 및 Oculus Quest 타이틀이 기본 지원을 제공합니다.",
    },
    [LanCode.Spanish]: {
      title: "Experiencias con TactSuit",
      description:
        "VR, PC, Película y Música - Experiencias con TactSuit. Hasta ahora, más de 250 títulos de SteamVR y Oculus Quest, incluidos Ghosts of Tabor, Breachers, Arizona Sunshine2, Onward y Pavlov, ofrecen soporte nativo.",
    },
  },
  /**
   * `/games/pc` · `/games/vr` 전용 (#6105) — 종전엔 두 페이지가 `/games` 접두 매칭으로 같은 「Games」 문구였다(영어 포함).
   * 문구는 각 페이지 머리말(`games` ns 「Your PC games, now with your whole body」 · 「Feel Popular VR Games Through
   * Your Body」)의 언어별 번역값으로 시작한다. VR 250+ 는 종전 「Games」 문구의 수치다.
   */
  GamesPc: {
    [LanCode.Chinese]: {
      title: "支持 TactSuit 的 PC 游戏 | bHaptics",
      description:
        "你熟悉的 PC 游戏，用全身来感受。除了已支持的 PC 游戏，还能通过 Key-to-Haptics 和 Audio-to-Haptics 以触感畅玩更多 PC 游戏。",
    },
    [LanCode.English]: {
      title: "PC Games with Haptics - bHaptics TactSuit",
      description:
        "Your PC games, now with your whole body. Explore supported PC games and enjoy even more with Key-to-Haptics and Audio-to-Haptics.",
    },
    [LanCode.French]: {
      title: "Jeux PC compatibles TactSuit | bHaptics",
      description:
        "Vos jeux PC, désormais ressentis par tout le corps. En plus des jeux PC compatibles, profitez de bien d'autres jeux grâce à Key-to-Haptics et Audio-to-Haptics.",
    },
    [LanCode.German]: {
      title: "PC-Spiele mit TactSuit-Unterstützung | bHaptics",
      description:
        "Deine PC-Spiele, jetzt am ganzen Körper. Neben unterstützten PC-Spielen erlebst du mit Key-to-Haptics und Audio-to-Haptics noch mehr Spiele haptisch.",
    },
    [LanCode.Japanese]: {
      title: "TactSuit対応PCゲーム | bHaptics",
      description:
        "いつものPCゲームを、全身で。対応PCゲームはもちろん、Key-to-HapticsとAudio-to-Hapticsでさらに多くのPCゲームをハプティクスで楽しめます。",
    },
    [LanCode.Korean]: {
      title: "TactSuit 지원 PC 게임 | 비햅틱스",
      description:
        "하던 PC 게임 그대로, 온몸으로. 지원 PC 게임은 물론 Key-to-Haptics와 Audio-to-Haptics로 더 많은 PC 게임을 햅틱으로 즐길 수 있습니다.",
    },
    [LanCode.Spanish]: {
      title: "Juegos de PC compatibles con TactSuit | bHaptics",
      description:
        "Tus juegos de PC, ahora con todo el cuerpo. Además de los juegos de PC compatibles, disfruta de muchos más con Key-to-Haptics y Audio-to-Haptics.",
    },
  },
  GamesVr: {
    [LanCode.Chinese]: {
      title: "支持 TactSuit 的 VR 游戏 | bHaptics",
      description:
        "用全身感受热门 VR 游戏。Ghosts of Tabor、Breachers 等 250 多款已支持的 VR 游戏之外，还能通过 Audio-to-Haptics 畅玩更多 VR 游戏。",
    },
    [LanCode.English]: {
      title: "VR Games with Haptics - bHaptics TactSuit",
      description:
        "Feel popular VR games through your body. Explore 250+ supported VR games, including Ghosts of Tabor and Breachers, and play even more with Audio-to-Haptics.",
    },
    [LanCode.French]: {
      title: "Jeux VR compatibles TactSuit | bHaptics",
      description:
        "Ressentez les jeux VR populaires dans tout votre corps. Plus de 250 jeux VR compatibles, dont Ghosts of Tabor et Breachers, et bien d'autres avec Audio-to-Haptics.",
    },
    [LanCode.German]: {
      title: "VR-Spiele mit TactSuit-Unterstützung | bHaptics",
      description:
        "Beliebte VR-Spiele mit dem ganzen Körper spüren. Über 250 unterstützte VR-Spiele wie Ghosts of Tabor und Breachers – und mit Audio-to-Haptics noch viele mehr.",
    },
    [LanCode.Japanese]: {
      title: "TactSuit対応VRゲーム | bHaptics",
      description:
        "人気のVRゲームを全身で感じる。Ghosts of TaborやBreachersなど250以上の対応VRゲームに加え、Audio-to-Hapticsでさらに多くのVRゲームを楽しめます。",
    },
    [LanCode.Korean]: {
      title: "TactSuit 지원 VR 게임 | 비햅틱스",
      description:
        "인기 VR 게임을 바로 몸으로 느껴보세요. Ghosts of Tabor, Breachers 등 250개 이상의 지원 VR 게임은 물론 Audio-to-Haptics로 더 많은 VR 게임을 즐길 수 있습니다.",
    },
    [LanCode.Spanish]: {
      title: "Juegos VR compatibles con TactSuit | bHaptics",
      description:
        "Siente los juegos de VR más populares con todo el cuerpo. Más de 250 juegos VR compatibles, como Ghosts of Tabor y Breachers, y muchos más con Audio-to-Haptics.",
    },
  },
  /**
   * `/games/pc|vr/games` · `/games/pc|vr/software` 전용 — 종전엔 네 페이지가 `/games` 접두 매칭으로 같은 「Games」였다.
   * 문구는 각 페이지 머리말의 기존 번역값(`games` ns 「Find the PC/VR Game You Want to Play」 · 「Explore supported…」,
   * 「Start PC Gaming with bHaptics Player」 · 「Connect TactSuit to PC Games with bHaptics Player」, `software` ns 「Connect
   * TactSuit and bHaptics devices for SteamVR-based VR games…」)이다.
   */
  GamesPcList: {
    [LanCode.Chinese]: {
      title: "寻找你想玩的 PC 游戏 | bHaptics",
      description:
        "除了已支持的 PC 游戏，还可通过 Key-to-Haptics 和 Audio-to-Haptics 畅玩更多 PC 游戏。",
    },
    [LanCode.English]: {
      title: "Find the PC Game You Want to Play - bHaptics TactSuit",
      description:
        "Explore supported PC games and enjoy more PC titles with Key-to-Haptics and Audio-to-Haptics.",
    },
    [LanCode.French]: {
      title: "Trouvez le jeu PC que vous voulez jouer | bHaptics",
      description:
        "Découvrez les jeux PC compatibles et profitez de plus de titres PC avec Key-to-Haptics et Audio-to-Haptics.",
    },
    [LanCode.German]: {
      title: "Finde das PC-Spiel, das du spielen willst | bHaptics",
      description:
        "Entdecke unterstützte PC-Spiele und erlebe mit Key-to-Haptics und Audio-to-Haptics noch mehr PC-Titel.",
    },
    [LanCode.Japanese]: {
      title: "プレイしたい PC ゲームを探す | bHaptics",
      description:
        "対応 PC ゲームはもちろん、Key-to-Haptics と Audio-to-Haptics でさらに多くの PC ゲームを楽しめます。",
    },
    [LanCode.Korean]: {
      title: "원하는 PC 게임을 찾아보세요 | 비햅틱스",
      description:
        "지원 PC 게임은 물론, Key-to-Haptics와 Audio-to-Haptics로 더 많은 PC 게임을 즐길 수 있습니다.",
    },
    [LanCode.Spanish]: {
      title: "Encuentra el juego de PC que quieres jugar | bHaptics",
      description:
        "Explora los juegos de PC compatibles y disfruta de más títulos de PC con Key-to-Haptics y Audio-to-Haptics.",
    },
  },
  GamesVrList: {
    [LanCode.Chinese]: {
      title: "寻找你想玩的 VR 游戏 | bHaptics",
      description:
        "浏览已支持的 VR 游戏，并通过 Audio-to-Haptics 以触感体验更多 VR 作品。",
    },
    [LanCode.English]: {
      title: "Find the VR Game You Want to Play - bHaptics TactSuit",
      description:
        "Explore supported VR games and enjoy more VR titles in haptics with Audio-to-Haptics.",
    },
    [LanCode.French]: {
      title: "Trouvez le jeu VR que vous voulez jouer | bHaptics",
      description:
        "Découvrez les jeux VR compatibles et profitez de plus de titres VR en haptique avec Audio-to-Haptics.",
    },
    [LanCode.German]: {
      title: "Finde das VR-Spiel, das du spielen willst | bHaptics",
      description:
        "Entdecke unterstützte VR-Spiele und erlebe mit Audio-to-Haptics noch mehr VR-Titel haptisch.",
    },
    [LanCode.Japanese]: {
      title: "プレイしたい VR ゲームを探す | bHaptics",
      description:
        "対応 VR ゲームを見て、Audio-to-Haptics でさらに多くの VR タイトルをハプティクスで楽しめます。",
    },
    [LanCode.Korean]: {
      title: "원하는 VR 게임을 찾아보세요 | 비햅틱스",
      description:
        "지원 VR 게임은 물론, Audio-to-Haptics로 더 많은 VR 게임을 햅틱으로 즐길 수 있습니다.",
    },
    [LanCode.Spanish]: {
      title: "Encuentra el juego de VR que quieres jugar | bHaptics",
      description:
        "Explora los juegos de VR compatibles y disfruta de más títulos de VR con háptica gracias a Audio-to-Haptics.",
    },
  },
  GamesPcSoftware: {
    [LanCode.Chinese]: {
      title: "用 bHaptics Player 开始 PC 游戏 | bHaptics",
      description:
        "用 bHaptics Player 连接 TactSuit 与 PC 游戏。你熟悉的 PC 游戏，用全身来感受。",
    },
    [LanCode.English]: {
      title: "Start PC Gaming with bHaptics Player - bHaptics TactSuit",
      description:
        "Connect TactSuit to PC games with bHaptics Player. Your PC games, now with your whole body.",
    },
    [LanCode.French]: {
      title: "Lancez le jeu PC avec bHaptics Player | bHaptics",
      description:
        "Connectez le TactSuit aux jeux PC avec bHaptics Player. Vos jeux PC, désormais ressentis par tout le corps.",
    },
    [LanCode.German]: {
      title: "Mit dem bHaptics Player ins PC-Gaming starten | bHaptics",
      description:
        "TactSuit mit dem bHaptics Player an PC-Spiele anbinden. Deine PC-Spiele, jetzt am ganzen Körper.",
    },
    [LanCode.Japanese]: {
      title: "bHaptics PlayerでPCゲームを始める | bHaptics",
      description:
        "bHaptics PlayerでTactSuitとPCゲームをつなぐ。いつものPCゲームを、全身で。",
    },
    [LanCode.Korean]: {
      title: "bHaptics Player로 PC 게임을 시작하세요 | 비햅틱스",
      description:
        "PC 게임과 TactSuit를 연결하는 bHaptics Player. 하던 PC 게임 그대로, 온몸으로 느껴보세요.",
    },
    [LanCode.Spanish]: {
      title: "Empieza a jugar en PC con bHaptics Player | bHaptics",
      description:
        "Conecta el TactSuit a los juegos de PC con bHaptics Player. Tus juegos de PC, ahora con todo el cuerpo.",
    },
  },
  GamesVrSoftware: {
    [LanCode.Chinese]: {
      title: "SteamVR 版 bHaptics Player | bHaptics",
      description:
        "在基于 SteamVR 的 VR 游戏中连接 TactSuit 与 bHaptics 设备。在电脑上管理设备连接与触感设置。",
    },
    [LanCode.English]: {
      title: "bHaptics Player for SteamVR - bHaptics TactSuit",
      description:
        "Connect TactSuit and bHaptics devices for SteamVR-based VR games. Manage device connection and haptic settings from your PC.",
    },
    [LanCode.French]: {
      title: "bHaptics Player pour SteamVR | bHaptics",
      description:
        "Connectez TactSuit et vos appareils bHaptics pour les jeux VR basés sur SteamVR. Gérez la connexion et les réglages haptiques depuis votre PC.",
    },
    [LanCode.German]: {
      title: "bHaptics Player für SteamVR | bHaptics",
      description:
        "Verbinde TactSuit und bHaptics-Geräte für SteamVR-basierte VR-Spiele. Geräteverbindung und Haptik-Einstellungen verwaltest du am PC.",
    },
    [LanCode.Japanese]: {
      title: "SteamVR向けbHaptics Player | bHaptics",
      description:
        "SteamVRベースのVRゲームでTactSuitとbHapticsデバイスを接続します。デバイス接続とハプティック設定をPCから管理できます。",
    },
    [LanCode.Korean]: {
      title: "SteamVR용 bHaptics Player | 비햅틱스",
      description:
        "SteamVR 기반 VR 게임에서 TactSuit 과 bHaptics 기기를 연결하세요. 기기 연결과 햅틱 설정을 PC 에서 관리합니다.",
    },
    [LanCode.Spanish]: {
      title: "bHaptics Player para SteamVR | bHaptics",
      description:
        "Conecta TactSuit y los dispositivos bHaptics para juegos de VR basados en SteamVR. Gestiona la conexión y los ajustes hápticos desde tu PC.",
    },
  },
};
